import React, { useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import "./Sidebar.css";
import {
  DashboardIcon,
  FileQuestionIcon,
  FileTextIcon,
  GalleryIcon,
  LockPersonIcon,
  MessageSquareCodeIcon,
  MessageSquareIcon,
  RegObjectGroupIcon,
  UserCheckIcon,
  UsersIcon,
} from "../../icons/reactIcons";
import { useAppDispatch, useAppSelector } from "../../redux/store/hooks";
import { fetchAdminPermissions } from "../../redux/slices/permissionsSlice";
import SidebarSkeleton from "./SidebarSkeleton";

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { permissions, loading } = useAppSelector((state) => state.permissions);

  useEffect(() => {
    if (permissions.size === 0) {
      dispatch(fetchAdminPermissions());
    }
  }, [dispatch, permissions.size]);

  const hasPermission = (key?: string) => {
    if (!key) return true;
    return permissions.has(key);
  };

  const menu = useMemo(
    () => [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: <DashboardIcon />,
      },
      {
        heading: "User",
        items: [
          {
            label: "Users",
            path: "/users",
            icon: <UsersIcon />,
            permission: "user.users",
          },
          {
            label: "User Request",
            path: "/user-request",
            icon: <UserCheckIcon />,
            permission: "user.user request",
          },
          {
            label: "User Group",
            path: "/user-group",
            icon: <RegObjectGroupIcon />,
            permission: "user.user group",
          },
          {
            label: "Delivered Messages",
            path: "/messages",
            icon: <MessageSquareIcon />,
            permission: "user.delivered messages",
          },
        ],
      },
      {
        heading: "Library",
        items: [
          {
            label: "Lessons",
            path: "/lessons",
            icon: <FileTextIcon />,
            permission: "library.lessons",
          },
          {
            label: "Resource",
            path: "/resources",
            icon: <GalleryIcon />,
            permission: "library.resource",
          },
          {
            label: "Questions",
            path: "/questions",
            icon: <FileQuestionIcon />,
            permission: "library.questions",
          },
        ],
      },
      {
        heading: "Settings",
        items: [
          {
            label: "Message Template",
            path: "/message-template",
            icon: <MessageSquareCodeIcon />,
            permission: "settings.message template",
          },
          {
            label: "Access Control",
            path: "/access-control",
            icon: <LockPersonIcon />,
            permission: "settings.access control",
          },
        ],
      },
    ],
    [],
  );

  if (loading) return <SidebarSkeleton />;

  return (
    <aside className="sidebar">
      <div className="sidebar-menu">
        {menu.map((section, idx) =>
          section.items ? (
            (() => {
              const visibleItems = section.items.filter((item) =>
                hasPermission(item.permission),
              );

              if (visibleItems.length === 0) return null;

              return (
                <div key={idx} className="sidebar-section">
                  <div className="sidebar-heading">{section.heading}</div>
                  {visibleItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        isActive ? "sidebar-link active" : "sidebar-link"
                      }
                    >
                      <span className="sidebar-icon">{item.icon}</span>
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              );
            })()
          ) : (
            <React.Fragment key={section.path}>
              <NavLink
                to={section.path}
                className={({ isActive }) =>
                  isActive ? "sidebar-link active" : "sidebar-link"
                }
              >
                <span className="sidebar-icon">{section.icon}</span>
                {section.label}
              </NavLink>
              <div className="sidebar-separator"></div>
            </React.Fragment>
          ),
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
